import React, { createContext, useReducer, ReactNode } from "react"
import { PreAuthAnswersResponse } from "./usePreAuthAnswers"
import { PreAuthResponse } from "./useCreatePreAuth"

export interface PreAuthFormState {
  journeyType?: string
  journeySubType?: string
  receiveCareFromEligibleProvider?: string
  isStorageFees?: string
  whoReceivedCare?: string
  howManyIUICycles?: string
  isMedicationExpenses?: string
  intendedCarrier?: string
  isInfertilityDx?: string
  isMedicallyNecessary?: string
  isStepchildAdoption?: string
  whatKindFertilityCare?: string
  totalCost?: number
}

interface PreAuthServerState {
  hasFertilityCareCoverage?: boolean
  hasFerilityPreservationCoverage?: boolean
  hasDonorAssistanceCoverage?: boolean
  hasGestationalCarrierCoverage?: boolean
  isCarrotRxRequired?: boolean
  hasFertilityCareRxCoverage?: boolean
  hasElectiveFertilityCareCoverage?: boolean
  hasInfertilityDxCoverage?: boolean
  hasMedicallyNecessaryFertilityCareCoverage?: boolean
  hasElectivePreservationCoverage?: boolean
  hasPreservationRxCoverage?: boolean
  isMedicallyNecessaryPreservationCovered?: boolean
  hasCompletedIvfRequirements?: boolean
  isGestationalCarrierRxCovered?: boolean
  checkPartnerEligibility?: (whoReceivedCare: string) => string
}

export type PreAuthFormStateAction =
  | { type: "UPDATE_FIELD"; field: keyof PreAuthFormState; value: string }
  | { type: "RESET_STATE" }

export type PreAuthAnswersResponseStateAction =
  | { type: "SET_RESPONSE"; response: PreAuthAnswersResponse }
  | { type: "CLEAR_RESPONSE" }

export type PreAuthResponseStateAction =
  | { type: "SET_RESPONSE"; response: PreAuthResponse }
  | { type: "CLEAR_RESPONSE" }

const initialState: PreAuthFormState = {}
const initialAnswersResponseState: PreAuthAnswersResponse | null = null
const initialPreAuthResponseState: PreAuthResponse | null = null

export function preAuthFormStateReducer(formState: PreAuthFormState, action: PreAuthFormStateAction): PreAuthFormState {
  switch (action.type) {
    case "UPDATE_FIELD":
      return { ...formState, [action.field]: action.value }
    case "RESET_STATE":
      return initialState
    default:
      return formState
  }
}

export function preAuthAnswersResponseStateReducer(
  preAuthAnswersResponse: PreAuthAnswersResponse | null,
  action: PreAuthAnswersResponseStateAction
): PreAuthAnswersResponse | null {
  switch (action.type) {
    case "SET_RESPONSE":
      return action.response
    case "CLEAR_RESPONSE":
      return null
    default:
      return preAuthAnswersResponse
  }
}

export function preAuthResponseStateReducer(
  preAuthResponse: PreAuthResponse | null,
  action: PreAuthResponseStateAction
): PreAuthResponse | null {
  switch (action.type) {
    case "SET_RESPONSE":
      return action.response
    case "CLEAR_RESPONSE":
      return null
    default:
      return preAuthResponse
  }
}

interface CarrotCardCostSharePreAuthContextType {
  formState: PreAuthFormState
  dispatchFormState: React.Dispatch<PreAuthFormStateAction>
  serverState: PreAuthServerState
  preAuthAnswersResponse: PreAuthAnswersResponse
  dispatchPreAuthAnswersResponseState: React.Dispatch<PreAuthAnswersResponseStateAction>
  preAuthResponse: PreAuthResponse
  dispatchPreAuthResponse: React.Dispatch<PreAuthResponseStateAction>
}

export const CarrotCardCostSharePreAuthContext = createContext<CarrotCardCostSharePreAuthContextType | null>(null)

export function CarrotCardCostShareProvider({
  children,
  serverState
}: {
  children: ReactNode
  serverState: PreAuthServerState
}): JSX.Element {
  const [formState, dispatchFormState] = useReducer(preAuthFormStateReducer, initialState)
  const [preAuthAnswersResponse, dispatchPreAuthAnswersResponseState] = useReducer(
    preAuthAnswersResponseStateReducer,
    initialAnswersResponseState
  )
  const [preAuthResponse, dispatchPreAuthResponse] = useReducer(
    preAuthResponseStateReducer,
    initialPreAuthResponseState
  )

  return (
    <CarrotCardCostSharePreAuthContext.Provider
      value={{
        formState,
        dispatchFormState,
        serverState,
        preAuthAnswersResponse,
        dispatchPreAuthAnswersResponseState,
        preAuthResponse,
        dispatchPreAuthResponse
      }}
    >
      {children}
    </CarrotCardCostSharePreAuthContext.Provider>
  )
}
